/**
 * Lockdown mode — public-only site gate.
 *
 * When `VITE_LOCKDOWN_MODE=true` is set at build time, the SPA hides the
 * nav and redirects every non-public route to `/teams`, so the public
 * team-browsing surface can be shared before the coaching platform ships.
 *
 * Build-time only: flipping this requires a rebuild + redeploy of
 * `web/dist/`, not just a server restart. This is a soft gate (UI hiding
 * + client redirects) — the hidden surfaces stay auth-gated server-side.
 *
 * `/login` and `/admin` are reachable in lockdown so an admin can still
 * sign in and reach backend management. That's safe because it's only a
 * UI gate: `AdminShell` runs its own auth guard (anonymous → `/login`,
 * non-admin → bounced out) and every backend route stays auth-gated.
 */
export const LOCKDOWN_MODE = import.meta.env.VITE_LOCKDOWN_MODE === "true";

/** Pathname prefixes that stay reachable in lockdown mode.
 *
 * `/teams/$teamSlug/live` (per-team live) is covered by the `/teams`
 * prefix; the legacy global `/live` route was removed once multi-team
 * shipped. */
const ALLOWED_PREFIXES = [
  "/teams",
  "/match",
  "/login",
  "/register",
  "/invite",
  "/verify-email",
  "/reset-password",
  "/admin",
];

export function isLockdownAllowedPath(pathname: string): boolean {
  return ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}
